/**
 * @description API services for products-services module
 * @author Omar Sánchez Martínez
 * @since 2025-08-02
 */

import { apiMunzen } from '@/app/api/apiMunzen/apiMunzen';
import { DEFAULT_ITEMS_PER_PAGE, DEFAULT_PAGE } from '@/app/config/pagination.config';
import type { ApiResponse } from '@/common/types';
import type {
  ProductService,
  ProductServiceForm,
  ProductServiceResponse,
  ProductServiceSearchParams,
} from '../types/productService.interface';

/**
 * @author Omar Sánchez Martínez
 * @since 2025-08-02
 * @description get paginated list of products/services
 */
export const fetchProductServicesService = async (
  params: Partial<ProductServiceSearchParams> = {},
  options?: { signal?: AbortSignal },
): Promise<ProductServiceResponse> => {
  const {
    page = DEFAULT_PAGE,
    items_perpage = DEFAULT_ITEMS_PER_PAGE,
    order_by,
    order_direction,
  } = params;

  // Build query string dynamically to avoid sending undefined params
  const searchParams = new URLSearchParams({
    page: page.toString(),
  });

  if (items_perpage) {
    searchParams.append('items_perpage', items_perpage.toString());
  }

  if (order_by) {
    searchParams.append('order_by', order_by);
  }

  if (order_direction) {
    searchParams.append('order_direction', order_direction);
  }

  const url = `/products-services?${searchParams.toString()}`;

  const response = await apiMunzen.get<ProductServiceResponse>(url, { signal: options?.signal });
  return response.data;
};

/**
 * @author Omar Sánchez Martínez
 * @since 2025-08-02
 * @description search products/services with pagination using POST endpoint
 */
export const searchProductsServicesService = async (
  params: ProductServiceSearchParams,
  options?: { signal?: AbortSignal },
): Promise<ProductServiceResponse> => {
  const {
    page = DEFAULT_PAGE,
    items_perpage = DEFAULT_ITEMS_PER_PAGE,
    data_search,
    order_by,
    order_direction,
  } = params;

  const response = await apiMunzen.post<ProductServiceResponse>(
    `/products-services/search?page=${page}`,
    {
      data_search: data_search ?? '', // Ensure empty string if undefined
      items_perpage,
      paginated_search: true,
      order_by,
      order_direction,
    },
    { signal: options?.signal },
  );

  return response.data;
};

/**
 * @author Omar Sánchez Martínez
 * @since 2025-08-02
 * @description get product/service by id
 */
export const fetchProductServiceByIdService = async (id: number): Promise<ProductService> => {
  const response = await apiMunzen.get<ApiResponse<ProductService>>(`/products-services/${id}`);
  return response.data.data;
};

/**
 * @author Omar Sánchez Martínez
 * @since 2025-08-02
 * @description search products/services without pagination (used in billing for autocomplete)
 */
export const searchProductServicesWithoutPaginationService = async (
  searchValue: string,
): Promise<ProductService[]> => {
  const response = await apiMunzen.post<ApiResponse<ProductService[]>>(
    '/products-services/search',
    {
      data_search: searchValue,
      paginated_search: false,
    },
  );

  return response.data.data;
};

/**
 * @author Omar Sánchez Martínez
 * @since 2025-08-02
 * @description validate if internal code is unique for products/services
 */
export const validateInternalCodeUniqueService = async (
  internal_code: string,
  signal?: AbortSignal,
  productServiceId?: number,
): Promise<boolean> => {
  const data = {
    internal_code,
    product_service_id: productServiceId,
  };

  const response = await apiMunzen.post<ApiResponse<{ is_unique: boolean }>>(
    '/products-services/internal-code-unique',
    data,
    {
      signal,
    },
  );
  return response.data.data.is_unique;
};

/**
 * @author Omar Sánchez Martínez
 * @since 2025-08-02
 * @description validate if description is unique for products/services
 */
export const validateDescriptionUniqueService = async (
  description: string,
  signal?: AbortSignal,
  productServiceId?: number,
): Promise<boolean> => {
  const data = {
    description,
    product_service_id: productServiceId,
  };

  const response = await apiMunzen.post<ApiResponse<{ is_unique: boolean }>>(
    '/products-services/description-unique',
    data,
    { signal },
  );
  return response.data.data.is_unique;
};

/**
 * @author Omar Sánchez Martínez
 * @since 2025-08-02
 * @description create a new product/service
 */
export const createProductServiceService = async (
  productServiceData: ProductServiceForm,
): Promise<ProductService> => {
  const response = await apiMunzen.post<ApiResponse<ProductService>>(
    '/products-services',
    productServiceData,
  );
  return response.data.data;
};

/**
 * @author Omar Sánchez Martínez
 * @since 2025-08-02
 * @description update an existing product/service
 */
export const updateProductServiceService = async (
  id: number,
  productServiceData: ProductServiceForm,
): Promise<ProductService> => {
  const response = await apiMunzen.put<ApiResponse<ProductService>>(
    `/products-services/${id}`,
    productServiceData,
  );
  return response.data.data;
};

/**
 * @author Omar Sánchez Martínez
 * @since 2025-08-02
 * @description delete a product/service
 */
export const deleteProductServiceService = async (id: number): Promise<{ success: boolean }> => {
  const response = await apiMunzen.delete<ApiResponse<{ success: boolean }>>(
    `/products-services/${id}`,
  );
  return response.data;
};

/**
 * @author Omar Sánchez Martínez
 * @since 2025-08-02
 * @description export products/services to excel file
 */
export const exportProductServicesService = async (): Promise<void> => {
  const response = await apiMunzen.get<Blob>('/products-services/export', {
    responseType: 'blob',
  });

  // Create blob and download file
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'Productos_Servicios.xlsx';

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
